import { enforce, omitWhen, test } from 'vest';

import {
  isEndpointUrl,
  isTimeZone,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface ShijiService {
  apiEndpointUrl: string;
  clientId: string;
  clientSecret: string;
  user: string;
  password: string;
  propertyCode: string;
  propertyId: string;
  defaultCheckOutTime: string;
  timezone: string;
}

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

const SERVICE = 'currentConfig.service';

const defaultConfig = (): IntegrationConfig => ({
  service: {
    apiEndpointUrl: '',
    clientId: '',
    clientSecret: '',
    user: '',
    password: '',
    propertyCode: '',
    propertyId: '',
    defaultCheckOutTime: '11:00',
    timezone: '',
  },
  features: {},
  cron: [],
  webhooks: [{ name: 'standard', enabled: true }],
});

export const shiji: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig.service as unknown as ShijiService;

    test(`${SERVICE}.apiEndpointUrl`, t('validations.required'), () => {
      enforce(service.apiEndpointUrl).isNotBlank();
    });

    omitWhen(!service.apiEndpointUrl, () => {
      test(`${SERVICE}.apiEndpointUrl`, t('validations.url'), () => {
        enforce(isEndpointUrl(service.apiEndpointUrl)).isTruthy();
      });
    });

    for (const field of [
      'clientId',
      'clientSecret',
      'user',
      'password',
      'propertyCode',
    ] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        enforce(service[field]).isNotBlank();
      });
    }

    test(`${SERVICE}.defaultCheckOutTime`, t('validations.required'), () => {
      enforce(service.defaultCheckOutTime).isNotBlank();
    });

    omitWhen(!service.defaultCheckOutTime, () => {
      test(`${SERVICE}.defaultCheckOutTime`, t('validations.invalid'), () => {
        enforce(service.defaultCheckOutTime).matches(TIME_OF_DAY);
      });
    });

    test(`${SERVICE}.timezone`, t('validations.required'), () => {
      enforce(service.timezone).isNotBlank();
    });

    omitWhen(!service.timezone, () => {
      test(`${SERVICE}.timezone`, t('validations.invalid'), () => {
        enforce(isTimeZone(service.timezone)).isTruthy();
      });
    });
  },
};
